import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { resolveLdtOrderDetailView } from '@ldt/features/orders/utils/resolve_ldt_order_detail_view';
import { orderKeys } from 'src/constants/orders';
import { useUser } from 'src/hooks/use_auth';
import { UploadState, UploadStatus, useUpload } from 'src/hooks/use_upload';
import {
  LdtOrderDetailFieldsFragment,
  SlideImageScannerType,
  LdtUploadedFileFieldsFragment,
  UploadedFileCategory,
  UploadedFileStatus,
} from 'src/types';

interface UseLdtFileUploadsOptions {
  order: LdtOrderDetailFieldsFragment;
  orderRepository: LdtOrderRepository;
  onFileCategoryUpdate?: (
    category: UploadedFileCategory,
    file?: LdtUploadedFileFieldsFragment,
    fileObject?: File,
  ) => void;
  onError?: (message: string) => void;
}

interface UseLdtFileUploadsReturn {
  filesByCategory: Partial<
    Record<UploadedFileCategory, LdtUploadedFileFieldsFragment[]>
  >;
  getFilesForCategory: (
    category: UploadedFileCategory,
  ) => LdtUploadedFileFieldsFragment[];
  handleFileSelected: (
    file: File,
    category: UploadedFileCategory,
  ) => Promise<void>;
  handleFileRemoved: (fileId: string) => Promise<void>;
  fileObjectsRef: RefObject<Map<string, File>>;
  getUploadsForOrder: (orderId: string) => UploadState[];
  isFileDeleting: (fileId: string) => boolean;
}

/**
 * Custom hook for managing file uploads across multiple categories in LDT forms.
 * Handles file state, upload progress, completion tracking, and file removal.
 *
 * @param options - Configuration options
 * @param options.order - The current order (used to load existing files)
 * @param options.orderRepository - Repository for reading order data (cache/refetch only; cancel/delete go through useUpload)
 * @param options.onFileCategoryUpdate - Optional callback when a file in a specific category is updated
 * @param options.onError - Optional error handler
 * @returns File management functions and state
 */
export const useLdtFileUploads = ({
  order,
  orderRepository,
  onFileCategoryUpdate,
  onError,
}: UseLdtFileUploadsOptions): UseLdtFileUploadsReturn => {
  const { organization } = useParams();
  const userInfo = useUser();

  // Track File objects by filename for validation purposes
  const fileObjectsRef = useRef<Map<string, File>>(new Map());

  // Use the existing upload hook (same as slide image uploads)
  const {
    uploadFile: uploadFileWithProgress,
    getUploadsForOrder,
    isFileDeleting,
    removeFileUpload,
  } = useUpload();

  const queryClient = useQueryClient();

  // Watch the order query to react to cache updates (e.g., after upload completion)
  // When completeUploadMutation updates the cache, this will update
  const { data: cachedOrder } = useQuery<LdtOrderDetailFieldsFragment | null>({
    queryKey: orderKeys.detail(order.id),
    queryFn: () =>
      orderRepository.getOrderById(order.id, {
        view: resolveLdtOrderDetailView(),
      }),
    enabled: !!order.id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: () => {
      return order;
    },
    initialDataUpdatedAt: () => {
      const queryState = queryClient.getQueryState(orderKeys.detail(order.id));
      return queryState?.dataUpdatedAt;
    },
  });

  // Derive files by category from cache so we stay in sync (no useEffect → setState lag)
  const filesByCategory = useMemo(() => {
    const orderToLoad = cachedOrder ?? null;
    if (!orderToLoad?.uploadedFiles?.edges) {
      return {
        [UploadedFileCategory.TestRequisitionForm]: [],
        [UploadedFileCategory.FaceSheet]: [],
        [UploadedFileCategory.PathologyReport]: [],
        [UploadedFileCategory.Insurance]: [],
        [UploadedFileCategory.ConsultReport]: [],
        [UploadedFileCategory.Attestation]: [],
        [UploadedFileCategory.Other]: [],
        [UploadedFileCategory.CancelDoc]: [],
      } as Partial<
        Record<UploadedFileCategory, LdtUploadedFileFieldsFragment[]>
      >;
    }
    const loaded: Partial<
      Record<UploadedFileCategory, LdtUploadedFileFieldsFragment[]>
    > = {
      [UploadedFileCategory.TestRequisitionForm]: [],
      [UploadedFileCategory.FaceSheet]: [],
      [UploadedFileCategory.PathologyReport]: [],
      [UploadedFileCategory.Insurance]: [],
      [UploadedFileCategory.ConsultReport]: [],
      [UploadedFileCategory.Attestation]: [],
      [UploadedFileCategory.Other]: [],
      [UploadedFileCategory.CancelDoc]: [],
    };
    orderToLoad.uploadedFiles.edges.forEach((edge) => {
      if (edge?.node && loaded[edge.node.category]) {
        loaded[edge.node.category]!.push(edge.node);
      }
    });
    return loaded;
  }, [cachedOrder]);

  // Track previous file state to detect when new files are added
  // Initialize with files from the initial order to avoid false positives on mount
  const getInitialPreviousFiles = useCallback(() => {
    const initial: Partial<Record<UploadedFileCategory, Set<string>>> = {};
    if (order.uploadedFiles?.edges) {
      order.uploadedFiles.edges.forEach((edge) => {
        if (edge?.node) {
          const category = edge.node.category;
          if (!initial[category]) {
            initial[category] = new Set();
          }
          initial[category]!.add(edge.node.id);
        }
      });
    }
    return initial;
  }, [order.uploadedFiles]);

  const previousFilesRef = useRef<
    Partial<Record<UploadedFileCategory, Set<string>>>
  >(getInitialPreviousFiles());

  // React to order data changes in cache (e.g., after upload completion or file removal)
  useEffect(() => {
    // Get the latest cache data directly (not from useQuery which might be stale)
    const latestCachedOrder =
      queryClient.getQueryData<LdtOrderDetailFieldsFragment | null>([
        'order',
        order.id,
      ]);

    if (!latestCachedOrder?.uploadedFiles?.edges) {
      return;
    }

    // Build current file state by category
    const currentFiles: Partial<Record<UploadedFileCategory, Set<string>>> = {};
    latestCachedOrder.uploadedFiles.edges.forEach((edge) => {
      if (edge?.node) {
        const category = edge.node.category;
        if (!currentFiles[category]) {
          currentFiles[category] = new Set();
        }

        currentFiles[category]!.add(edge.node.id);
      }
    });

    // Detect new files and removed files by comparing with previous state
    const allCategories = new Set([
      ...Object.keys(currentFiles),
      ...Object.keys(previousFilesRef.current),
    ]);

    allCategories.forEach((category) => {
      const categoryEnum = category as UploadedFileCategory;
      const currentFileIds = currentFiles[categoryEnum] || new Set();
      const previousFileIds =
        previousFilesRef.current[categoryEnum] || new Set();

      // Find files that are new (in current but not in previous)
      currentFileIds.forEach((fileId) => {
        if (!previousFileIds.has(fileId)) {
          // New file detected - find it and notify
          const newFile = latestCachedOrder.uploadedFiles?.edges.find(
            (edge) => edge?.node?.id === fileId,
          )?.node;

          if (newFile && onFileCategoryUpdate) {
            // Try to find the File object from the upload
            const fileObject = fileObjectsRef.current.get(newFile.filename);
            onFileCategoryUpdate(categoryEnum, newFile, fileObject);
          }
        }
      });

      // Find files that are deleted/removed from the order (in previous but not in current)
      previousFileIds.forEach((fileId) => {
        if (!currentFileIds.has(fileId)) {
          // File removed - notify with undefined
          if (onFileCategoryUpdate) {
            onFileCategoryUpdate(categoryEnum, undefined, undefined);
          }
        }
      });
    });

    // Update previous files state for next comparison
    previousFilesRef.current = currentFiles;
  }, [cachedOrder, onFileCategoryUpdate, queryClient, order.id]);

  // Helper to merge upload states with completed files for display
  const getFilesForCategory = useCallback(
    (category: UploadedFileCategory): LdtUploadedFileFieldsFragment[] => {
      const completedFiles = filesByCategory[category] || [];
      const currentUploads = order.id
        ? getUploadsForOrder(order.id.toString())
        : [];

      // Convert upload states to UploadedFile objects for display
      // Only show uploads that are still in progress - completed uploads are in cache
      const uploadFiles = currentUploads
        .filter((upload: UploadState) => upload.category === category)
        .filter(
          (upload: UploadState) =>
            upload.status === UploadStatus.Queued ||
            upload.status === UploadStatus.InProgress ||
            upload.status === UploadStatus.Failed,
        )
        // Also filter out any uploads that are already marked as Complete in cache
        // This prevents showing "Uploading" for files that have already completed
        .filter((upload: UploadState) => {
          const cachedFile = completedFiles.find((f) => f.id === upload.fileId);
          // If file exists in cache with Complete status, don't show it as uploading
          return !(cachedFile?.status === UploadedFileStatus.Complete);
        })
        .map((upload: UploadState): LdtUploadedFileFieldsFragment => {
          const status =
            upload.status === UploadStatus.Failed
              ? UploadedFileStatus.Failed
              : upload.status === UploadStatus.InProgress
                ? UploadedFileStatus.InProgress
                : UploadedFileStatus.InProgress; // Queued shows as InProgress for UI

          return {
            id: upload.fileId,
            filename: upload.fileName,
            category: upload.category || category,
            status,
            progress: upload.progress,
            createdAt: new Date().toISOString(),
            isAutoGenerated: false,
            uploaderAuth0Id: userInfo?.id || null,
          };
        });

      // Prioritize upload files over completed files to show latest progress
      const uploadFileIds = new Set(
        uploadFiles.map((f: LdtUploadedFileFieldsFragment) => f.id),
      );
      const uniqueCompletedFiles = completedFiles.filter(
        (f: LdtUploadedFileFieldsFragment) => !uploadFileIds.has(f.id),
      );

      return [...uploadFiles, ...uniqueCompletedFiles];
    },
    [filesByCategory, order.id, getUploadsForOrder, userInfo?.id],
  );

  const handleFileSelected = useCallback(
    async (file: File, category: UploadedFileCategory) => {
      // Store File object for later use (especially for validation)
      fileObjectsRef.current.set(file.name, file);

      // Don't upload if there's no order ID

      // Use the upload hook for chunked uploads with progress tracking
      // Pass category to allow multiple files per order
      try {
        await uploadFileWithProgress(
          file,
          order.id,
          organization || '', // crossOrgId
          category, // Pass category to support multiple files per order
          SlideImageScannerType.GrundiumOcus_20, // Not used for regular files, but required
        );
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : 'File upload failed';
        if (onError) {
          onError(errorMsg);
        }
      }
    },
    [order.id, organization, uploadFileWithProgress, onError],
  );

  const handleFileRemoved = useCallback(
    async (fileId: string) => {
      await removeFileUpload(fileId, order.id);
    },
    [order.id, removeFileUpload],
  );

  return {
    filesByCategory,
    getFilesForCategory,
    handleFileSelected,
    handleFileRemoved,
    fileObjectsRef,
    getUploadsForOrder,
    isFileDeleting,
  };
};
